import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { ProductTileSkeleton } from '@hpstellar/core';

import { Helpers } from '../../core/src/helpers';
import ProductMap from '../../product/components/product-map';
import ProductTile from '../../product/components/product-tile';
import { getStoreAppProductProps } from '../../product/get-product-props';
import ProductFilter from '../../product/components//product-filter';
import ProductResults from '../../product/components/product-results';
import SearchResults from '../../search-filter/components/search-results';
import { withProductContainer } from '../../product/components/product-container';
import useProductPrice from '../../hooks/useProductPrice';
import useAddToCart from '../../hooks/useAddToCart';
import useProductCompare from '../../hooks/useProductCompare';
import { ColorSelectorContainer } from '../../hooks/useColorSelector';
//TODO: move this import if v2 is winner
import { getProdHideStatus, SORT_TYPES } from '../../product/components/product-list';
import useProductImpressions from '../../hooks/useProductImpressionsV2';
import useCartState from '../../hooks/useCartState';
import { memoizedFormatMetricValue, generateUniqueProductMapKey } from '../../metrics/metrics-helpers';
import StickyNav from './sale-page-tabs/sticky-nav';
import ABTest from '../../abtest';
import useTestFlags from '../../abtest/useTestFlags';
import { resolveABTestData } from './util';

const { memoize } = Helpers;
let loadedSkus = {};
const ProductItem = props => {
    let { onLazyLoad, ...rest } = props || {};
    let { product } = rest || {};
    let { sku } = product || {};
    useEffect(() => {
        if (typeof onLazyLoad === 'function') {
            onLazyLoad(sku);
        }
    }, []);

    return <ProductTile {...(rest || {})} />;
};
const productItem =
    (addToCart, compare, prices, list, addedToCart, cartId, jumpInfo, onLazyLoad) =>
    (product, key, enableAppPDP, className, productIndex, placeholder, page_level, position) => {
        const priceObj = prices[product.sku];
        let urlParams = Helpers.getPDPJumpId(product, jumpInfo);
        let { sku } = product || {};
        if (placeholder) {
            return (
                <div key={productIndex} data-sku={sku}>
                    <ProductTileSkeleton />
                </div>
            );
        }

        return (
            <ColorSelectorContainer key={product.sku} product={product}>
                {({ colorSelection }) => {
                    const productProps = getStoreAppProductProps(
                        product,
                        priceObj,
                        {
                            addToCart,
                            compare,
                            colorSelection,
                        },
                        { resizeImage: true, urlParams, hasPriceSeoText: true },
                        { list: list || page_level, cartId, position },
                    );
                    return (
                        <ProductItem
                            className={`product-tile ${className} product-analytic-sku-${sku}`}
                            {...productProps}
                            ratingSpaceReserved={true}
                            onLazyLoad={onLazyLoad}
                        />
                    );
                }}
            </ColorSelectorContainer>
        );
    };

const ProductMapContainer = ({ products, slug, pg, jumpInfo, analyticsBatchKey, gotFinalProducts, checkCondition }) => {
    const { prices } = useProductPrice(products, {
        withIntraDayDeals: true,
        returnEmptyPriceObj: false,
        useProductPromos: true,
    });
    const list = useSelector(state => {
        try {
            let tabName = state.slugInfo.components.productTab.productGroups.find(
                productGroup => productGroup && ((pg && productGroup.key === pg) || (!pg && productGroup.active)),
            ).name;

            return memoizedFormatMetricValue(`slp-${tabName}`);
        } catch (e) {}
    });
    const cartState = useCartState();
    const { cartId } = cartState || {};
    const vanityUrl = useSelector(state => state && state.slugInfo && state.slugInfo.vanityUrl);
    const compare = useProductCompare();
    const addToCart = useAddToCart(true);
    const [analyticProducts, setAnalyticProducts] = useState(null);
    const [batchKey, setBatchKey] = useState(analyticsBatchKey);
    const getLoadedProductsDebounced = debounce(() => {
        if (!gotFinalProducts) {
            return;
        }
        let lazyLoadedCount = 0;
        let skus = '';
        let filteredProducts =
            Array.isArray(products) &&
            products.reduce((allPrds, prd, idx) => {
                let { sku } = prd || {};
                if (checkCondition(prd) && sku && !/^user/i.test(sku)) {
                    let analyticsHasNotLazyLoaded = !loadedSkus[sku];
                    if (analyticsHasNotLazyLoaded) {
                        lazyLoadedCount++;
                        skus += sku;
                    }
                    allPrds.push({
                        ...(prd || {}),
                        analyticsHasNotLazyLoaded,
                        // gtmUniqueId: generateUniqueProductMapKey(
                        //     prd,
                        //     {
                        //         vanityUrl,
                        //         listPosition: idx + 1
                        //     }
                        // )
                    });
                }
                return allPrds;
            }, []);
        let currSkus =
            Array.isArray(analyticProducts) &&
            analyticProducts.reduce(
                (prdSkus, prd) => prdSkus + (prd && !prd.analyticsHasNotLazyLoaded ? prd.sku : ''),
                '',
            );
        let hasDiffProducts = !analyticProducts || currSkus !== skus;
        if (hasDiffProducts) {
            setAnalyticProducts(filteredProducts);
        }
    }, 800);

    const onLazyLoad = sku => {
        loadedSkus[sku] = 1;
        getLoadedProductsDebounced();
    };

    useEffect(() => {
        if (analyticsBatchKey) {
            setAnalyticProducts(null); //reset, trigger above effect
            setBatchKey(analyticsBatchKey);
        }
    }, [analyticsBatchKey]);

    useEffect(() => {
        return () => {
            loadedSkus = {};
        };
    }, []);

    useEffect(() => {
        if (gotFinalProducts) {
            getLoadedProductsDebounced();
        }
    }, [gotFinalProducts, products]);

    const productHasLoaded = prd => prd && prd.sku && loadedSkus[prd.sku];

    const lazyLoadKey =
        analyticProducts &&
        analyticProducts.reduce((allKeys, prd) => (productHasLoaded(prd) ? allKeys + prd.sku + '-' : allKeys), '');

    // Whenever product cards are lazy loaded, the products parameter changes to trigger impressions
    const productsWithAnalytics = useProductImpressions({
        products,
        list,
        componentKey: 'productTab',
        key: vanityUrl,
        isEmpty: !analyticProducts || analyticProducts.length < 1,
        batchKey,
        isGlobalTracking: true,
        impressionKey: pg,
        // lazyLoadKey triggers impressions, it contains skus of all lazyloaded product tiles. Selector checks if sku is lazy loads (in loadedSkus)
        selector: productHasLoaded,
        lazyLoadKey,
    });

    const prds = Array.isArray(productsWithAnalytics) ? productsWithAnalytics : products;

    return (
        <ProductMap
            products={prds}
            productItem={productItem(addToCart, compare, prices, list, null, cartId, jumpInfo, onLazyLoad)}
            enableAppPDP={true}
            productItemClassName={'product-tile'}
            customPlaceHolder={true}
            v2
        />
    );
};

const ProductSortABTestWrapper = props => {
    const { slpSort } = useTestFlags(['slpSort']);
    const { enabled: ssrLoader } = slpSort || {};
    return (
        <ABTest experimentID="SLPSortOrder" ssrRender={true}>
            {data => {
                const { products: apiProducts, sortValue, ...restProps } = props;
                const { getABTestProducts } = data;
                const { products, loadingList } = resolveABTestData(getABTestProducts, apiProducts, sortValue) || {
                    products: apiProducts,
                };
                //when server side test is enabled, show loader until client side test is resolved
                const isLoadingList = typeof loadingList === 'undefined' ? ssrLoader : loadingList;
                return (
                    <div className={isLoadingList ? 'loading-list' : ''}>
                        <ProductMapContainer {...restProps} products={products} loadingList={isLoadingList} />
                    </div>
                );
            }}
        </ABTest>
    );
};

class ProductList extends React.PureComponent {
    static whyDidYouRender = true;

    constructor(props) {
        super(props);
        this.changeSort = this.changeSort.bind(this);
        this.tabStyle = {};
        this.prefetchInterval = null;
    }

    componentDidMount() {
        const { pg, productCat = {}, fetchProducts, prefetchTabs } = this.props;
        if (!productCat.products || productCat.products.length === 0) {
            fetchProducts(pg);
        } else if (prefetchTabs) {
            this.prefetchProduct();
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.slug == this.props.slug &&
            this.props.pg &&
            prevProps.pg !== this.props.pg &&
            !this.props.productCat.products.length
        ) {
            prevProps.fetchProducts(this.props.pg);
        } else if (this.props.prefetchTabs) {
            this.prefetchProduct();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.prefetchInterval);
    }

    prefetchProduct = () => {
        const { pg, prefetchProducts } = this.props;
        const [vanityUrl] = pg.split(':');
        //if there are still keys left to fetch, prefetch them
        if (prefetchProducts) {
            this.prefetchInterval = setTimeout(() => prefetchProducts(vanityUrl), 400);
        }
    };

    changeSort(value) {
        const { analyticsLabel } = SORT_TYPES.find(s => s && s.value === value) || {};
        this.props.sortProducts(value, this.props.pg, analyticsLabel);
        this.props.scrollTop();
    }

    prdFilterCondition = memoize((searchResults, query) => (prd, params) => {
        let { prdClass } = prd;
        const { autoHideType, disableAutoHide } = this.props;
        //if filters are enabled ,ignore auto hide functionality
        if (searchResults && searchResults.length > 0) {
            //only show UDF when search is enabled if not filtering on anything
            if (prdClass === 'USER_DEFINED') {
                return Object.keys(query || {}).length === 0;
            }
            return searchResults.indexOf(prd.sku) > -1;
        }
        return getProdHideStatus(autoHideType, disableAutoHide, prd, params);
    });

    render() {
        const {
            fixedHeader,
            productCat,
            sortGtmActions,
            leftMenu,
            ToggleFilter,
            pg,
            slug,
            slugKey,
            tabProps,
            disableSearch,
        } = this.props;
        const { products, key, sortValue } = productCat;
        const shiftProductsClassName = fixedHeader ? ' shift-down' : '';
        const jumpInfo = {
            slugKey,
            pg: pg && pg.split(':').pop(),
        };
        return (
            <SearchResults>
                {({ results, query, searchInProgress }) => (
                    <ProductFilter
                        products={products}
                        condition={this.prdFilterCondition(!disableSearch && !searchInProgress ? results : null, query)}
                    >
                        {(filteredProducts, childProps) => {
                            const productListLength = filteredProducts.filter(
                                p => p.prdClass !== 'USER_DEFINED',
                            ).length;
                            const { priceReady, checkCondition } = childProps || {};
                            const gotFinalProducts = priceReady && !searchInProgress;
                            return (
                                <>
                                    <ProductResults>
                                        <StickyNav
                                            tabProps={tabProps}
                                            changeSort={this.changeSort}
                                            sortOptions={SORT_TYPES}
                                            productCat={productCat}
                                            productListLength={productListLength}
                                            gotFinalProducts={gotFinalProducts}
                                            ToggleFilter={ToggleFilter}
                                            sortGtmActions={sortGtmActions}
                                            disableSearch={disableSearch}
                                        />
                                        <div className={'products-list' + shiftProductsClassName}>
                                            {leftMenu}
                                            <ProductSortABTestWrapper
                                                key="slp-products"
                                                products={filteredProducts}
                                                pg={pg}
                                                slug={slug}
                                                jumpInfo={jumpInfo}
                                                analyticsBatchKey={key}
                                                gotFinalProducts={gotFinalProducts}
                                                checkCondition={checkCondition}
                                                sortValue={sortValue}
                                            />
                                        </div>
                                    </ProductResults>
                                </>
                            );
                        }}
                    </ProductFilter>
                )}
            </SearchResults>
        );
    }
}

ProductList.defaultProps = {
    productCat: { products: [], sortValue: 'rank.recommended' },
    filtersResults: { hits: { hits: null } },
    userInfo: {},
};

export default withProductContainer(ProductList);
